<template>
  <div class="market">
    <div class="container">
      <div class="market__wrapper">
        <div class="market__top">
          <div class="market__top-left">
            <div class="market__top-block">{{ activeCategory ? activeCategory.name : t('market.title') }}</div>
            <div class="market__top-buttons">
              <div class="market__top-button" @click="setNameOrdering">
                <p>{{ t('market.sortBy') }} <span>{{ t('market.name') }}</span></p>
                <span class="market__top-button-inner">
                <span
                    :class="orderBy === 'name' ? 'market__top-button-small' : 'market__top-button-active'"
                />
                <span />
                <span
                    :class="orderBy === 'name' ? 'market__top-button-active' : 'market__top-button-small'"
                />
              </span>
              </div>
              <div class="market__top-button" @click="setPriceOrdering">
                <p>{{ t('market.sortBy') }} <span>{{ t('market.price') }}</span></p>
                <span class="market__top-button-inner">
                <span
                    :class="orderBy === 'price' ? 'market__top-button-small' : 'market__top-button-active'"
                />
                <span />
                <span
                    :class="orderBy === 'price' ? 'market__top-button-active' : 'market__top-button-small'"
                />
              </span>
              </div>
            </div>
          </div>
          <form class="market__form" @submit.prevent="search">
            <input
                class="market__form-input"
                :placeholder="t('fields.headerSearch')"
                v-model="searchInput"
            />
          </form>
        </div>
        <div class="market__main" v-if="products.length > 0">
          <div class="market__grid" v-if="products.length > 0">
            <product-card
                v-for="product in products"
                :key="product.node.uuid"
                :product="product.node"
            />
          </div>
          <div class="market__grid" v-else-if="isLoading">
            <product-card-skeleton v-for="idx in 12" :key="idx" />
          </div>
          <div class="market__grid" v-if="products.length > 0">
            <product-card
                v-for="product in products"
                :key="product.node.uuid"
                :product="product.node"
            />
          </div>

          <button
              v-if="pageInfo.hasNextPage && !isLoading"
              class="market__show-more"
              @click="getMoreProducts"
          >
            {{ t('market.showMore') }}
          </button>

          <div v-if="isLoading && products.length > 0" class="market__loading-more">
            Loading...
          </div>
        </div>
        <p v-else class="market__empty">{{ t('market.empty') }}</p>
      </div>
    </div>
    <transition name="opacity" mode="out-in">
      <product-view
          v-if="activeViewProduct"
          :product="activeViewProduct"
      />
    </transition>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {useI18n} from "vue-i18n";
import {useRoute} from "vue-router";
import {useRouter} from "vue-router";
import {Categories} from "@/core/api/categories.js";
import {Products} from "@/core/api/products.js";
import ProductCard from "@/components/cards/product-card.vue";
import ProductCardSkeleton from "@/components/skeletons/product-card-skeleton.vue";
import ProductView from "@/components/cards/product-view.vue";
import {useAppStore} from "@/stores/app.js";

const {t} = useI18n()
const route = useRoute()
const router = useRouter()
const appStore = useAppStore()


const categoryFromQuery = computed(() => {
  return route.query.categoriesSlugs ? route.query.categoriesSlugs : null
})

const activeCategory = ref([])

const getCategory = async () => {
  if (categoryFromQuery.value) {
    const res = await Categories.getCategoryBySlug(categoryFromQuery.value)
    activeCategory.value = res.data.categories.edges[0].node
  } else {
    activeCategory.value = null
  }
}

const isLoading = ref(false)


const searchInput = ref('')
const search = () => {
  router.push({
    name: 'search',
    params: {
      searchQuery: searchInput.value,
      locale: localStorage.getItem('digitgiftcode-user-locale') || 'en-gb'
    }
  })
}

const products = ref([])
const pageInfo = ref([])
const cursorForAfter = ref('0')
const orderBy = ref(route.query['orderBy'] ? String(route.query['orderBy']) : '')

const query = computed(() => {
  const categoriesParam = categoryFromQuery.value
      ? `categoriesSlugs=${categoryFromQuery.value}`
      : 'categoriesSlugs=';

  return `?${categoriesParam}&orderBy=${orderBy.value}`
})

const toggleOrdering = (type) => {
  const currentType = orderBy.value.startsWith('-') ? orderBy.value.slice(1) : orderBy.value;
  orderBy.value = (currentType === type) ? (orderBy.value.startsWith('-') ? type : `-${type}`) : type;
};
const setPriceOrdering = () => toggleOrdering('price');
const setNameOrdering = () => toggleOrdering('name');

const getProducts = async () => {
  isLoading.value = true
  cursorForAfter.value = '0'

  const res = await Products.getProducts(
      `after: "${cursorForAfter.value}"` +
      `, first: 20` +
      (categoryFromQuery.value ? `, categoriesSlugs: "${categoryFromQuery.value}"` : "") +
      `, orderBy: "${orderBy.value}"`
  )
  products.value = res.data?.products?.edges
  pageInfo.value = res.data?.products?.pageInfo

  isLoading.value = false
}

const getMoreProducts = async () => {
  if (isLoading.value || !pageInfo.value?.hasNextPage) return

  isLoading.value = true

  cursorForAfter.value = pageInfo.value.endCursor

  const res = await Products.getProducts(
      `after: "${cursorForAfter.value}"` +
      `, first: 20` +
      (categoryFromQuery.value ? `, categoriesSlugs: "${categoryFromQuery.value}"` : "") +
      `, orderBy: "${orderBy.value}"`
  )

  products.value.push(...res.data.products.edges)
  pageInfo.value = res.data.products.pageInfo

  isLoading.value = false
}

watch(
    () => query.value,
    async () => {
      products.value = [];
      history.pushState({}, '', route.path + query.value);
      await getProducts();
    }
)

watch(
    () => categoryFromQuery.value,
    async () => {
      await getCategory();
    },
    { deep: true }
)

const activeViewProduct = computed(() => {
  return appStore.activeViewProduct
})

onMounted( async () => {
  await getCategory()
  await getProducts()
})
</script>

<style lang="scss" scoped>
.market {
  padding-top: 170px;
  position: relative;

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 30px;
    padding-bottom: 30px;
    border-bottom: 1px solid #3B3B3B;

    &-left{
      display: flex;
      align-items: center;
      gap: 30px;
    }

    &-block {
      border-radius: 10px;
      background-color: #FFFFFF;
      padding: 10px 50px;

      border: 1px solid #3B3B3B;

      color: #3B3B3B;
      text-align: center;
      font-size: 20px;
      font-weight: 600;
      line-height: 25.801px;
    }

    &-buttons {
      display: flex;
      align-items: center;
      gap: 15px;
    }

    &-button {
      cursor: pointer;
      border-radius: 10px;
      background-color: #FFFFFF;
      display: flex;
      align-items: center;
      gap: 20px;
      padding: 11px 15px;
      transition: 0.2s;

      border: 1px solid #3B3B3B;

      @include hover {
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
      }

      & p {
        color: #3B3B3B;
        font-size: 14px;
        font-weight: 400;
        line-height: 18px;

        & span {
          font-weight: 700;
        }
      }

      &-inner {
        display: flex;
        align-items: start;
        flex-direction: column;
        justify-content: space-between;
        width: 40px;
        height: 23px;

        & span {
          height: 2px;
          background-color: #3B3B3B;
          border-radius: 0.5rem;
          width: 75%;
          transition: all 0.3s ease;
        }
      }

      &-small {
        width: 50% !important;
      }

      &-active {
        width: 100% !important;
      }
    }
  }

  &__form{
    background-image: url("@icons/search.svg");
    background-repeat: no-repeat;
    background-position: left 25px top 50%;
    background-color: #FFFFFF;
    border: #3b3b3b solid 1px;
    border-radius: 30px;
    padding: 13px 10px 13px 46px;

    width: 450px;

    & input{
      width: 100%;
    }
  }

  &__main {
    margin-top: 50px;
  }

  &__grid {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(225px, 225px));
    column-gap: 20px;
    row-gap: 30px;
    justify-content: space-between;

    &-observer {
      width: 100%;
      height: 10px;
      background-color: transparent;
    }
  }

  &__empty {
    text-align: center;
    font-size: 20px;
    font-weight: 600;
    line-height: 26px;
    color: #212844;
  }
}

@media (max-width: 700px) {
  .market {

    &__form{
      width: 350px;
    }

    &__top {
      flex-direction: column;
      gap: 15px;

      &-left{
        flex-direction: column;
        align-items: unset;
        justify-content: center;
      }
    }
  }
}

@media (max-width: 550px) {
  .market {
    &__top {
      &-buttons {
        flex-direction: column;
      }
    }

    &__grid {
      justify-content: center;
    }
  }
}

.market {

  &__show-more {
    margin: 40px auto 0;
    display: block;
    padding: 12px 40px;
    border-radius: 10px;
    background: #ffffff;
    border: 1px solid #3B3B3B;
    cursor: pointer;
    font-weight: 600;
    transition: 0.2s;

    &:hover {
      box-shadow: 0 0 10px rgba(0,0,0,0.15);
    }
  }

  &__loading-more {
    text-align: center;
    margin-top: 20px;
    font-weight: 500;
  }
}

</style>